/**
 * TanStack Query on top of the typed client. Components use these hooks and
 * nothing else; the query keys are listed here so an import can invalidate
 * exactly what an import changes.
 */
import {
  BEOORDELINGEN,
  type Beoordeling,
  type Fase,
  type Labelfeed,
  type Listing,
  normaliseerZoekfilters,
  vergelijkEnergielabel,
  type Zoekfilters,
  type Zoekopdracht,
} from '@mamimap/shared';
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import {
  type Filter,
  isLokaleSortering,
  type ServerFilter,
  type Sortering,
  serverFilter,
} from '../filter-url';
import { isStoring } from './client';
import { api, type LabelfeedVraag } from './endpoints';
import { type PuntenCache, werkPuntenBij } from './oordeel-cache';
import type { Sessie } from './schemas';

export const sleutels = {
  sessie: ['sessie'] as const,
  datasets: ['datasets'] as const,
  zoekopdrachten: ['zoekopdrachten'] as const,
  fundaRust: ['fundaRust'] as const,
  fundaBlokkades: ['fundaBlokkades'] as const,
  gebieden: ['gebieden'] as const,
  buurten: ['buurten'] as const,
  zoekgebied: ['zoekgebied'] as const,
  labelfeed: (vraag: LabelfeedVraag) => ['labels', 'feed', vraag] as const,
  labelOverzicht: ['labels', 'overzicht'] as const,
  zoekgebiedSchatting: (filters: Zoekfilters) =>
    ['zoekgebied', 'schatting', normaliseerZoekfilters(filters)] as const,
  budgetGevolg: (filters: Zoekfilters) =>
    ['zoekgebied', 'budget-gevolg', normaliseerZoekfilters(filters)] as const,
  vanger: ['vanger'] as const,
  pushSleutel: ['pushSleutel'] as const,
  drieDSleutel: ['drieDSleutel'] as const,
  sportcity: ['sportcity'] as const,
  sportcityWijken: (code: string) => ['sportcityWijken', code] as const,
  woning: (id: string) => ['woning', id] as const,
  transacties: (lat: number, lng: number, straalM: number) =>
    ['transacties', lat.toFixed(5), lng.toFixed(5), straalM] as const,
  transactie: (id: string) => ['transactie', id] as const,
  taxatie: (woningId: string) => ['taxatie', woningId] as const,
  realworksPostcodes: (lat: number, lng: number, straalM: number) =>
    ['realworks-postcodes', lat.toFixed(5), lng.toFixed(5), straalM] as const,
  realworksZoekopdrachten: (woningId: string) => ['realworks-zoekopdrachten', woningId] as const,
  dossier: (woningId: string) => ['dossier', woningId] as const,
  fotoStand: ['fotoStand'] as const,
  fotos: (id: string) => ['fotos', id] as const,
  logboek: (id: string) => ['logboek', id] as const,
  bestemmingsplan: (lng: number, lat: number) => ['bestemmingsplan', lng, lat] as const,
  facetten: (filter: ServerFilter) => ['facetten', filter] as const,
  geojson: (filter: ServerFilter) => ['geojson', filter] as const,
  bereik: (filter: ServerFilter) => ['bereik', filter] as const,
  woningen: (filter: ServerFilter, sortering: Sortering, limiet: number) =>
    ['woningen', filter, sortering, limiet] as const,
};

/** Rows per page: a label sort or our own sieve needs the whole set, a server sort does not. */
const PAGINA = 100;
const PAGINA_LOKAAL = 500;
/** Ceiling on a locally sorted table, so a future 200k-row import cannot run away. */
const MAX_LOKAAL = 2000;

/**
 * Wie de sessie is. Een storing (503 database_onbereikbaar) is uitdrukkelijk iets
 * anders dan niet ingelogd: die proberen we het zelf opnieuw, en zolang dat niet
 * lukt toont de schil een melding in plaats van het inlogformulier.
 */
export function useSessie() {
  return useQuery({
    queryKey: sleutels.sessie,
    queryFn: () => api.sessie(),
    retry: (pogingen, fout) => isStoring(fout) && pogingen < 2,
    retryDelay: 800,
    refetchInterval: (query) => (isStoring(query.state.error) ? 5_000 : false),
    staleTime: 30_000,
  });
}

export function useInloggen() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: api.inloggen,
    onSuccess: () => client.invalidateQueries({ queryKey: sleutels.sessie }),
  });
}

/**
 * Uitloggen moet meteen te zien zijn. `clear()` gooit de cache leeg maar gooit
 * de sessie-query er ook uit, en dan krijgt de schil niets te horen: het
 * e-mailadres en de woningen van de vorige gebruiker bleven seconden in beeld
 * staan tot iets anders een hertekening afdwong. Nu zetten we de sessie zelf op
 * 'niet ingelogd' - dat is één melding aan de schil, dus het inlogformulier
 * staat er meteen - en ruimen we daarna alle overige gegevens op.
 */
export function useUitloggen() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: api.uitloggen,
    onSuccess: () => {
      client.setQueryData<Sessie>(sleutels.sessie, {
        ingelogd: false,
        gebruiker: null,
        verlooptOp: null,
      });
      client.removeQueries({
        predicate: (query) => query.queryKey[0] !== sleutels.sessie[0],
      });
    },
  });
}

/**
 * De zoekopdrachten met hun laatste ronde. Ververst zichzelf elke minuut zolang
 * het tabblad open staat: een ronde die net gelopen heeft hoort je niet te
 * ontgaan omdat je toevallig al keek.
 */
export function useZoekopdrachten() {
  return useQuery({
    queryKey: sleutels.zoekopdrachten,
    queryFn: () => api.zoekopdrachten(),
    refetchInterval: 60_000,
  });
}

/** De globale Funda-rust en het dagtotaal; ververst mee met de zoekopdrachten. */
export function useFundaRust() {
  return useQuery({
    queryKey: sleutels.fundaRust,
    queryFn: () => api.fundaRust(),
    refetchInterval: 60_000,
  });
}

/** Het blokkadelog (MA-46); ververst mee met de stand. */
export function useFundaBlokkades() {
  return useQuery({
    queryKey: sleutels.fundaBlokkades,
    queryFn: () => api.fundaBlokkades(),
    refetchInterval: 60_000,
  });
}

/** Funda's gebiedenlijst voor de buurtkiezer; beweegt zelden, dus lang vers. */
export function useGebieden() {
  return useQuery({
    queryKey: sleutels.gebieden,
    queryFn: () => api.gebieden(),
    staleTime: 10 * 60_000,
  });
}

/** De CBS-buurten voor de kaart van het zoekgebied; veranderen eens per jaar. */
export function useBuurten() {
  return useQuery({
    queryKey: sleutels.buurten,
    queryFn: () => api.buurten(),
    staleTime: 60 * 60_000,
  });
}

export function useZoekgebied() {
  return useQuery({
    queryKey: sleutels.zoekgebied,
    queryFn: () => api.zoekgebied(),
  });
}

/**
 * De schatting voor filters die nog niet zijn opgeslagen, zodat de teller
 * meebeweegt terwijl je ze bijstelt. Uit zolang de filters gelijk zijn aan de
 * opgeslagen: dan zegt de stand het al.
 */
export function useZoekgebiedSchatting(filters: Zoekfilters, aan: boolean) {
  return useQuery({
    queryKey: sleutels.zoekgebiedSchatting(filters),
    queryFn: () => api.zoekgebiedSchatting(filters),
    enabled: aan,
    placeholderData: keepPreviousData,
  });
}

/** Hoeveel woningen door het budget in deze filters uit de zoekopdrachten vallen; voor de bevestiging. */
export function useBudgetGevolg(filters: Zoekfilters) {
  return useQuery({
    queryKey: sleutels.budgetGevolg(filters),
    queryFn: () => api.budgetGevolg(filters),
    enabled: filters.maxVraagprijs != null,
    staleTime: 0,
  });
}

/**
 * Het zoekgebied opslaan. De zoekopdrachten veranderen mee (nieuw, aan, uit),
 * en hun feeds kunnen een nieuwe naam krijgen; beide lijsten opnieuw ophalen.
 */
export function useZetZoekgebied() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: api.zetZoekgebied,
    onSuccess: (uitslag) => {
      client.setQueryData(sleutels.zoekgebied, uitslag.stand);
      void client.invalidateQueries({ queryKey: sleutels.zoekopdrachten });
      void client.invalidateQueries({ queryKey: sleutels.datasets });
    },
  });
}

export function useZetZoekgebiedGrens() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: api.zetZoekgebiedGrens,
    onSuccess: (stand) => client.setQueryData(sleutels.zoekgebied, stand),
  });
}

/** Het dagplafond bijstellen (MA-32); het antwoord is de nieuwe stand van funda-rust. */
export function useZetFundaDagplafond() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: api.zetFundaDagplafond,
    onSuccess: (stand) => client.setQueryData(sleutels.fundaRust, stand),
  });
}

/** De vaste tijden en het budget per keer bijstellen (MA-46); het antwoord is de nieuwe stand. */
export function useZetFundaVenster() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: api.zetFundaVenster,
    onSuccess: (stand) => client.setQueryData(sleutels.fundaRust, stand),
  });
}

export function useMaakZoekopdracht() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: api.maakZoekopdracht,
    onSuccess: () => void client.invalidateQueries({ queryKey: sleutels.zoekopdrachten }),
  });
}

/**
 * Bewerken schrijft het antwoord meteen in de lijst: aan/uit zetten moet direct
 * zichtbaar zijn, en de server geeft de hele bijgewerkte rij terug.
 */
export function useBewerkZoekopdracht() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: api.bewerkZoekopdracht,
    onSuccess: (bijgewerkt) => {
      client.setQueryData<Zoekopdracht[]>(sleutels.zoekopdrachten, (huidig) =>
        huidig?.map((rij) => (rij.id === bijgewerkt.id ? bijgewerkt : rij)),
      );
      /* Een hernoemde zoekopdracht hernoemt zijn feed, en die staat in de datasettabel. */
      void client.invalidateQueries({ queryKey: sleutels.datasets });
    },
  });
}

/**
 * De fotoreeks van één woning, alleen zolang het popupje open staat. Een foto
 * verandert niet, dus de lijst mag lang vers blijven.
 */
/** Het advertentielogboek van één woning; pas opgehaald als iemand erom vraagt. */
export function useLogboek(id: string | null) {
  return useQuery({
    queryKey: sleutels.logboek(id ?? ''),
    queryFn: () => api.logboek(id ?? ''),
    enabled: id !== null,
    staleTime: 5 * 60_000,
  });
}

/**
 * Het bestemmingsplan op het punt van een woning, pas als het blok openklapt.
 * Een plan verandert zelden; een uur vers is ruim genoeg.
 */
export function useBestemmingsplan(punt: [number, number] | null) {
  const [lng, lat] = punt ?? [0, 0];
  return useQuery({
    queryKey: sleutels.bestemmingsplan(lng, lat),
    queryFn: () => api.bestemmingsplan(lng, lat),
    enabled: punt !== null,
    staleTime: 60 * 60_000,
    retry: 1,
  });
}

export function useFotos(id: string | null) {
  return useQuery({
    queryKey: sleutels.fotos(id ?? ''),
    queryFn: () => api.fotos(id ?? ''),
    enabled: id !== null,
    staleTime: 10 * 60_000,
  });
}

/** Hoe het met de foto's staat; ververst zichzelf zolang het Datasets-scherm open is. */
export function useFotoStand() {
  return useQuery({
    queryKey: sleutels.fotoStand,
    queryFn: () => api.fotoStand(),
    refetchInterval: 60_000,
  });
}

export function useFacetten(filter: Filter) {
  const server = serverFilter(filter);
  return useQuery({
    queryKey: sleutels.facetten(server),
    queryFn: () => api.facetten(server),
    placeholderData: (vorige) => vorige,
  });
}

export function useGeojson(filter: Filter) {
  const server = serverFilter(filter);
  return useQuery({
    queryKey: sleutels.geojson(server),
    queryFn: () => api.geojson(server),
    placeholderData: (vorige) => vorige,
  });
}

/**
 * Eén woning als kaartpunt, los van het filter (MA-59), voor `/kaart?woning=`.
 * De sleutel valt onder `geojson` met alle oordelen als filter: zo werkt een
 * keuring in het popupje dit punt bij zoals elk ander, en haalt hem niet weg.
 */
export function usePunt(id: string | null) {
  return useQuery({
    queryKey: ['geojson', { beoordelingen: [...BEOORDELINGEN], woning: id }] as const,
    queryFn: () => api.punt(id ?? ''),
    enabled: id !== null,
  });
}

/**
 * Het werkelijke bereik binnen dit filter, voor de hints in de van/tot-velden.
 * Vier rijen van één, dus goedkoop; en hij blijft lang vers, want het bereik van
 * een dataset beweegt alleen bij een import.
 */
export function useBereik(filter: Filter) {
  const server = serverFilter(filter);
  return useQuery({
    queryKey: sleutels.bereik(server),
    queryFn: () => api.bereik(server),
    staleTime: 5 * 60_000,
    placeholderData: (vorige) => vorige,
  });
}

/**
 * De rijen van de tabel. Adres, postcode, prijs, oppervlakte en kamers lopen over
 * de keyset-cursor van de server; alleen de labelvolgorde en de zeef op 'zonder
 * coördinaat' kent de API niet, en daarvoor haalt de tabel het filter in zijn
 * geheel op - tot `MAX_LOKAAL`, en boven dat plafond zegt het scherm het.
 */
export function useWoningen(filter: Filter, sortering: Sortering) {
  const server = serverFilter(filter);
  const zonderPunt = filter.zonderCoordinaat === true;
  const lokaal = isLokaleSortering(sortering);
  /** Alles nodig: het scherm zeeft of ordent zelf, dus het moet alles gezien hebben. */
  const alles = lokaal || zonderPunt;
  const limiet = alles ? PAGINA_LOKAAL : PAGINA;

  const query = useInfiniteQuery({
    queryKey: sleutels.woningen(server, sortering, limiet),
    queryFn: ({ pageParam }) =>
      api.woningen({ filter: server, sortering, limiet, cursor: pageParam ?? undefined }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (laatste) => laatste.volgendeCursor ?? undefined,
    placeholderData: (vorige) => vorige,
  });

  /** Wat er binnen ditzelfde filter van de server kwam, vóór onze eigen zeef. */
  const geladen = query.data?.pages.reduce((som, pagina) => som + pagina.data.length, 0) ?? 0;

  const rijen = useMemo<Listing[]>(() => {
    const opgehaald = query.data?.pages.flatMap((pagina) => pagina.data) ?? [];
    const gezeefd = zonderPunt
      ? opgehaald.filter((woning) => woning.lat === null || woning.lng === null)
      : opgehaald;
    if (!lokaal) return gezeefd;
    const richting = sortering.endsWith('_op') ? 1 : -1;
    return [...gezeefd].sort((a, b) => {
      // Geen label is geen waarde: die rijen horen onderaan, welke kant de
      // sortering ook op staat. Anders opent 'Label aflopend' met de woningen
      // waarvan we juist niets weten.
      if ((a.energielabel === null) !== (b.energielabel === null)) {
        return a.energielabel === null ? 1 : -1;
      }
      return richting * vergelijkEnergielabel(a.energielabel, b.energielabel);
    });
  }, [query.data, lokaal, zonderPunt, sortering]);

  /** Rijen die het contract niet haalden; het scherm meldt ze onderaan de tabel. */
  const overgeslagen = query.data?.pages.reduce((som, pagina) => som + pagina.overgeslagen, 0) ?? 0;

  const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;
  useEffect(() => {
    if (!alles || !hasNextPage || isFetchingNextPage) return;
    if (geladen >= MAX_LOKAAL) return;
    void fetchNextPage();
  }, [alles, hasNextPage, isFetchingNextPage, fetchNextPage, geladen]);

  /**
   * Het plafond is geraakt en er is nog meer: dan klopt een telling niet meer en
   * hoort de tabel dat te zeggen in plaats van stil een deel te tonen.
   */
  const beperkt = alles && hasNextPage && geladen >= MAX_LOKAAL;

  return {
    rijen,
    overgeslagen,
    /**
     * Zeeft het scherm zelf, dan is het totaal van de server niet het totaal van
     * de tabel: dat is pas bekend als alles binnen is.
     */
    totaal: zonderPunt
      ? hasNextPage
        ? null
        : rijen.length
      : (query.data?.pages[0]?.totaal ?? null),
    volledigGeladen: !hasNextPage,
    beperkt,
    maxLokaal: MAX_LOKAAL,
    ...query,
  };
}

/**
 * Ons oordeel over één woning zetten.
 *
 * De cache wordt bijgewerkt in plaats van weggegooid. Dat is geen optimalisatie
 * maar het verschil tussen bruikbaar en niet: een `invalidateQueries` op de kaart
 * haalt alle 928 punten opnieuw op, MapLibre hertekent, en het popupje dat je net
 * openklikte valt dicht - bij elke klik op 'Kansje'. Het antwoord van de server
 * is de bijgewerkte woning, dus die stand kan er rechtstreeks in.
 *
 * Op de kaart gaat dat per weergave, want wat er met het punt gebeurt hangt af
 * van het filter van die weergave (MA-34): hoort de woning er nog bij, dan
 * springt haar stand om; een afkeuring in een weergave zonder filter op
 * afgekeurd haalt het punt meteen weg. Zie `werkPuntenBij`. In de tabel blijft
 * een rij staan met het nieuwe icoon tot de volgende lading, zodat de rijen
 * niet onder je muis verschuiven terwijl je een lijst afwerkt.
 *
 * Eén soort query moet wél opnieuw: die zelf op `beoordelingen` selecteert. Daar
 * verandert niet de inhoud van een rij maar of hij er nog bij hoort - een kansje
 * dat je afkeurt hoort van het Kansjes-scherm te verdwijnen, en dat kan een
 * gewijzigd veld in de cache niet regelen.
 */
export function useBeoordelen() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (invoer: { id: string; beoordeling: Beoordeling }) => api.beoordeel(invoer),
    onSuccess: (woning) => {
      /* Het filter staat als tweede deel in de sleutel - zie `sleutels.geojson`. */
      for (const [sleutel] of client.getQueriesData<PuntenCache>({ queryKey: ['geojson'] })) {
        const filter = (sleutel[1] ?? {}) as ServerFilter;
        client.setQueryData<PuntenCache>(sleutel, (oud) => werkPuntenBij(oud, filter, woning));
      }

      client.setQueriesData<WoningenCache>({ queryKey: ['woningen'] }, (oud) =>
        oud
          ? {
              ...oud,
              pages: oud.pages.map((pagina) => ({
                ...pagina,
                data: pagina.data.map((rij) => (rij.id === woning.id ? woning : rij)),
              })),
            }
          : oud,
      );

      void client.invalidateQueries({ predicate: (query) => selecteertOpOordeel(query.queryKey) });
      /* De telling 'afgekeurd' in de balk hoort meteen te kloppen. */
      void client.invalidateQueries({ queryKey: ['facetten'] });
    },
  });
}

/**
 * De fase van een kansje zetten (MA-51). Meteen in de cache, nog vóór de server
 * antwoordt: een kaartje dat je naar een andere kolom sleept hoort daar te
 * blijven liggen en niet eerst terug te springen. Weigert de server, dan haalt
 * de tabel zijn rijen opnieuw op en ligt het kaartje weer waar het lag.
 */
export function useFase() {
  const client = useQueryClient();
  const vervang = (id: string, wijzig: (rij: Listing) => Listing) =>
    client.setQueriesData<WoningenCache>({ queryKey: ['woningen'] }, (oud) =>
      oud
        ? {
            ...oud,
            pages: oud.pages.map((pagina) => ({
              ...pagina,
              data: pagina.data.map((rij) => (rij.id === id ? wijzig(rij) : rij)),
            })),
          }
        : oud,
    );
  return useMutation({
    mutationFn: (invoer: { id: string; fase: Fase; volgorde?: number }) => api.zetFase(invoer),
    onMutate: async (invoer) => {
      await client.cancelQueries({ queryKey: ['woningen'] });
      const nu = new Date();
      vervang(invoer.id, (rij) => ({
        ...rij,
        fase: invoer.fase,
        faseOp: rij.fase === invoer.fase ? rij.faseOp : nu.toISOString(),
        // Zonder plaats bovenaan, net als de server doet.
        faseVolgorde: invoer.volgorde ?? -nu.getTime() / 1000,
      }));
    },
    onSuccess: (woning) => vervang(woning.id, () => woning),
    onError: () => void client.invalidateQueries({ queryKey: ['woningen'] }),
  });
}

/**
 * De hoofdfoto van een woning kiezen (MA-51). Net als de fase meteen in de
 * cache, in de tabelrijen én in de kaartpunten, zodat het popupje en het
 * Kanban-kaartje allebei bij de nieuwe foto beginnen.
 */
export function useHoofdfoto() {
  const client = useQueryClient();
  const zet = (id: string, hoofdfoto: number | null) => {
    client.setQueriesData<WoningenCache>({ queryKey: ['woningen'] }, (oud) =>
      oud
        ? {
            ...oud,
            pages: oud.pages.map((pagina) => ({
              ...pagina,
              data: pagina.data.map((rij) => (rij.id === id ? { ...rij, hoofdfoto } : rij)),
            })),
          }
        : oud,
    );
    client.setQueriesData<PuntenCache>({ queryKey: ['geojson'] }, (oud) =>
      oud
        ? {
            ...oud,
            features: oud.features.map((f) =>
              f.properties.id === id ? { ...f, properties: { ...f.properties, hoofdfoto } } : f,
            ),
          }
        : oud,
    );
  };
  return useMutation({
    mutationFn: (invoer: { id: string; volgnummer: number | null }) => api.zetHoofdfoto(invoer),
    onMutate: (invoer) => zet(invoer.id, invoer.volgnummer),
    onSuccess: (woning) => zet(woning.id, woning.hoofdfoto),
    onError: () => {
      void client.invalidateQueries({ queryKey: ['woningen'] });
      void client.invalidateQueries({ queryKey: ['geojson'] });
    },
  });
}

/**
 * Een groep in één keer keuren of weghalen. Geen schrijven in de cache zoals bij
 * één woning: na een handeling over tientallen rijen is opnieuw ophalen
 * goedkoper en zekerder dan tientallen rijen bijwerken. Alles wat woningen toont
 * of telt wordt ongeldig: de tabel, de kaart, de facetten en de datasettellingen.
 */
function alleWoningenOngeldig(client: ReturnType<typeof useQueryClient>) {
  void client.invalidateQueries({ queryKey: ['woningen'] });
  void client.invalidateQueries({ queryKey: ['geojson'] });
  void client.invalidateQueries({ queryKey: ['facetten'] });
  void client.invalidateQueries({ queryKey: ['bereik'] });
  void client.invalidateQueries({ queryKey: sleutels.datasets });
  void client.invalidateQueries({ queryKey: sleutels.fotoStand });
}

export function useBeoordelenBulk() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: api.beoordeelBulk,
    onSuccess: () => alleWoningenOngeldig(client),
  });
}

export function useVerwijderWoningen() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: api.verwijderWoningen,
    onSuccess: () => alleWoningenOngeldig(client),
  });
}

export function useVerwijderDataset() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: api.verwijderDataset,
    onSuccess: () => alleWoningenOngeldig(client),
  });
}

/** De tabelcache zoals haar queryFn hem achterlaat; de puntencache staat in `oordeel-cache.ts`. */
type WoningenCache = {
  pages: {
    data: Listing[];
    volgendeCursor: string | null;
    limiet: number;
    totaal: number | null;
    overgeslagen: number;
  }[];
  pageParams: unknown[];
};

/**
 * Selecteert deze query zelf op een oordeel? Dan bepaalt het oordeel welke rijen
 * erin zitten en is bijwerken niet genoeg. Het filter staat als tweede deel in
 * elke sleutel hierboven - zie `sleutels`.
 */
function selecteertOpOordeel(sleutel: readonly unknown[]): boolean {
  const filter = sleutel[1];
  if (typeof filter !== 'object' || filter === null) return false;
  const beoordelingen = (filter as { beoordelingen?: unknown }).beoordelingen;
  return Array.isArray(beoordelingen) && beoordelingen.length > 0;
}

export function useDatasets() {
  return useQuery({ queryKey: sleutels.datasets, queryFn: () => api.datasets() });
}

export function useImporteren() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: api.importeer,
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: sleutels.datasets });
      void client.invalidateQueries({ queryKey: ['woningen'] });
      void client.invalidateQueries({ queryKey: ['facetten'] });
      void client.invalidateQueries({ queryKey: ['geojson'] });
    },
  });
}

/**
 * Waar de vanger op de server is. Vaak verversen zolang er een ronde loopt - de
 * voortgang telt per pagina - en rustig als hij slaapt.
 */
export function useVangerStand() {
  return useQuery({
    queryKey: sleutels.vanger,
    queryFn: () => api.vangerStand(),
    staleTime: 0,
    refetchInterval: (query) => {
      const staat = query.state.data?.staat;
      return staat === 'start' || staat === 'bezig' || staat === 'captcha' ? 3000 : 15_000;
    },
  });
}

export function useVangerStart() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: api.vangerStart,
    onSuccess: (stand) => client.setQueryData(sleutels.vanger, stand),
  });
}

export function useVangerStop() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: api.vangerStop,
    onSuccess: (stand) => client.setQueryData(sleutels.vanger, stand),
  });
}

export function useVangerInstellingen() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: api.vangerInstellingen,
    onSuccess: (stand) => client.setQueryData(sleutels.vanger, stand),
  });
}

/** De publieke VAPID-sleutel; verandert alleen bij een nieuwe configuratie. */
export function usePushSleutel() {
  return useQuery({
    queryKey: sleutels.pushSleutel,
    queryFn: () => api.pushSleutel(),
    staleTime: 60 * 60_000,
  });
}

/**
 * De Google-sleutel voor het 3D-paneel (MA-30), of null als de server er geen
 * heeft. Verandert alleen met een nieuwe .env op de server, dus één keer per
 * sessie ophalen is genoeg; zonder sleutel staat de knop er gewoon niet.
 */
export function useDrieDSleutel() {
  return useQuery({
    queryKey: sleutels.drieDSleutel,
    queryFn: () => api.drieDSleutel(),
    staleTime: Number.POSITIVE_INFINITY,
  });
}

/** De clubs en plaatsen van SportCity (MA-72): een vast bestand, dus één keer ophalen. */
export function useSportcityOverzicht() {
  return useQuery({
    queryKey: sleutels.sportcity,
    queryFn: () => api.sportcityOverzicht(),
    staleTime: Number.POSITIVE_INFINITY,
  });
}

/**
 * De wijken en buurten van één gemeente met hun CBS-cijfers (MA-73). Het CBS
 * ververst ze één keer per jaar, dus binnen een sessie hoeft het niet opnieuw.
 */
export function useSportcityWijken(gemeentecode: string | null) {
  return useQuery({
    queryKey: sleutels.sportcityWijken(gemeentecode ?? ''),
    queryFn: () => api.sportcityWijken(gemeentecode ?? ''),
    enabled: gemeentecode !== null,
    staleTime: Number.POSITIVE_INFINITY,
    retry: 1,
  });
}

/* --- Taxatie (MA-56) ------------------------------------------------------ */

export function useWoning(id: string | null) {
  return useQuery({
    queryKey: sleutels.woning(id ?? ''),
    queryFn: () => api.woning(id ?? ''),
    enabled: id !== null,
  });
}

export function useTransacties(punt: { lat: number; lng: number } | null, straalM = 1500) {
  return useQuery({
    queryKey: sleutels.transacties(punt?.lat ?? 0, punt?.lng ?? 0, straalM),
    queryFn: () => api.transacties({ lat: punt?.lat ?? 0, lng: punt?.lng ?? 0, straalM }),
    enabled: punt !== null,
    staleTime: 5 * 60_000,
  });
}

export function useTransactie(id: string | null) {
  return useQuery({
    queryKey: sleutels.transactie(id ?? ''),
    queryFn: () => api.transactie(id ?? ''),
    enabled: id !== null,
    staleTime: 10 * 60_000,
  });
}

export function useZetFotoLabel() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: api.zetFotoLabel,
    onSuccess: (_, invoer) =>
      client.invalidateQueries({ queryKey: sleutels.transactie(invoer.id) }),
  });
}

export function useTaxatie(woningId: string | null) {
  return useQuery({
    queryKey: sleutels.taxatie(woningId ?? ''),
    queryFn: () => api.taxatie(woningId ?? ''),
    enabled: woningId !== null,
  });
}

/** De postcodes rond het doelpand, om een zoekgebied voor Realworks aan te klikken (MA-66). */
export function useRealworksPostcodes(punt: { lat: number; lng: number } | null, straalM: number) {
  return useQuery({
    queryKey: sleutels.realworksPostcodes(punt?.lat ?? 0, punt?.lng ?? 0, straalM),
    queryFn: () => api.realworksPostcodes({ lat: punt?.lat ?? 0, lng: punt?.lng ?? 0, straalM }),
    enabled: punt !== null,
    staleTime: 60 * 60_000,
  });
}

/**
 * De zoekopdrachten van een woning. Zolang er een open is, kijkt hij elke tien
 * seconden of de extensie hem al heeft uitgevoerd.
 */
export function useRealworksZoekopdrachten(woningId: string | null) {
  return useQuery({
    queryKey: sleutels.realworksZoekopdrachten(woningId ?? ''),
    queryFn: () => api.realworksZoekopdrachten(woningId ?? ''),
    enabled: woningId !== null,
    refetchInterval: (query) =>
      query.state.data?.some((opdracht) => opdracht.status === 'open') ? 10_000 : false,
  });
}

function zoekopdrachtenOngeldig(client: ReturnType<typeof useQueryClient>) {
  return client.invalidateQueries({ queryKey: ['realworks-zoekopdrachten'] });
}

export function useMaakRealworksZoekopdrachten() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: api.maakRealworksZoekopdrachten,
    onSuccess: () => zoekopdrachtenOngeldig(client),
  });
}

export function useHeropenRealworksZoekopdracht() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: api.heropenRealworksZoekopdracht,
    onSuccess: () => zoekopdrachtenOngeldig(client),
  });
}

export function useVerwijderRealworksZoekopdracht() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: api.verwijderRealworksZoekopdracht,
    onSuccess: () => zoekopdrachtenOngeldig(client),
  });
}

export function useBewaarTaxatie() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: api.bewaarTaxatie,
    onSuccess: (uit, invoer) => client.setQueryData(sleutels.taxatie(invoer.woningId), uit),
  });
}

/** Wat de open bronnen over een woning zeggen; de API bewaart het een halve dag. */
export function useDossier(woningId: string | null) {
  return useQuery({
    queryKey: sleutels.dossier(woningId ?? ''),
    queryFn: () => api.dossier(woningId ?? ''),
    enabled: woningId !== null,
    staleTime: 60 * 60_000,
  });
}

export function useVernieuwDossier() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (woningId: string) => api.dossier(woningId, true),
    onSuccess: (dossier, woningId) => client.setQueryData(sleutels.dossier(woningId), dossier),
  });
}

/* --- labels (MA-61) --------------------------------------------------------- */

/** Nieuwe labelregistraties in het zoekgebied, met de klassen en m² uit `vraag`. */
export function useLabelfeed(vraag: LabelfeedVraag, aan = true) {
  return useQuery({
    queryKey: sleutels.labelfeed(vraag),
    queryFn: () => api.labelfeed(vraag),
    staleTime: 5 * 60_000,
    enabled: aan,
    placeholderData: keepPreviousData,
  });
}

/**
 * Een label afkeuren. Het verdwijnt meteen uit elke feed in de cache, zodat het
 * pilletje niet blijft staan tot de API terug is; daarna haalt alles opnieuw.
 */
export function useKeurLabelAf() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: api.keurLabelAf,
    onMutate: (id: number) => {
      client.setQueriesData<Labelfeed>({ queryKey: ['labels', 'feed'] }, (feed) =>
        feed ? { ...feed, rijen: feed.rijen.filter((rij) => rij.id !== id) } : feed,
      );
    },
    onSettled: () => client.invalidateQueries({ queryKey: ['labels'] }),
  });
}

/** Labels per buurt en per dag, en het deel dat daarna te koop kwam. */
export function useLabelOverzicht() {
  return useQuery({
    queryKey: sleutels.labelOverzicht,
    queryFn: () => api.labelOverzicht(),
    staleTime: 5 * 60_000,
  });
}
